/**
 * In-game right-side leaderboard panel.
 *
 * Auto-cycles between two modes every CYCLE_MS:
 *   • ALL_TIME  → all-time standings across every game session for this tenant
 *                 (the backend sends up to 200 rows; the panel ping-pong scrolls
 *                 through them).
 *   • SESSION   → standings inside the current Survey Says game
 *
 * Cross-fades between modes via CSS transitions on opacity. The streamer
 * always sees both views over time without needing to toggle anything.
 *
 * Reuses the shared `.sg-lblb*` styles from lobby-leaderboard.ts so the
 * visual language matches the lobby panels.
 */

import type { LbRow } from '@sg/shared';
import { fitText } from './fit-text.js';
import { ensureLobbyLbStyles } from './lobby-leaderboard.js';

export type LeaderboardMode = 'all_time' | 'session';

export interface SideLeaderboardHandle {
  el: HTMLDivElement;
  /** All-time top rows (typically 50). */
  setAllTimeRows: (rows: LbRow[]) => void;
  /** Session standings (current Survey Says game). */
  setSessionRows: (rows: LbRow[]) => void;
  /** Force a specific mode (useful for tests / manual override). */
  setMode: (mode: LeaderboardMode) => void;
  setVisible: (v: boolean) => void;
  dispose: () => void;
}

/** How long each mode is shown before cross-fading to the other. */
const CYCLE_MS = 12_000;
/** Duration of the title + body cross-fade. */
const FADE_MS = 360;

const VARIANT_STYLES = `
  @keyframes sg-sidelb-in {
    from { opacity: 0; transform: translateX(12px); }
    to   { opacity: 1; transform: translateX(0);    }
  }
  .sg-lblb[data-role="game-side-lb"] {
    position: absolute;
    top: 120px;
    left: 1500px;
    width: 380px;
    height: 840px;
    z-index: 50;
    pointer-events: none;
    animation: sg-sidelb-in 280ms ease-out;
  }
  .sg-lblb[data-role="game-side-lb"].hidden { display: none; }
  .sg-lblb[data-role="game-side-lb"] .sg-lblb-title {
    font-size: 1.1rem;
    letter-spacing: 0.24em;
    padding: 10px 8px 8px 8px;
    transition: opacity ${FADE_MS}ms ease;
  }
  .sg-lblb[data-role="game-side-lb"] .sg-lblb-head,
  .sg-lblb[data-role="game-side-lb"] .sg-lblb-row {
    grid-template-columns: 40px 1fr 86px;
  }
  .sg-lblb[data-role="game-side-lb"] .sg-lblb-head {
    font-size: 0.72rem;
    padding: 6px 12px 4px 12px;
  }
  .sg-lblb[data-role="game-side-lb"] .sg-lblb-row {
    padding: 8px 12px;
    font-size: 1.0rem;
  }
  .sg-lblb[data-role="game-side-lb"] .sg-lblb-rank {
    font-size: 1.1rem;
  }
  .sg-lblb[data-role="game-side-lb"] .sg-lblb-points {
    font-size: 1.05rem;
  }
  .sg-lblb[data-role="game-side-lb"] .sg-lblb-empty {
    padding: 20px 14px;
    font-size: 0.95rem;
    line-height: 1.4;
  }
  .sg-lblb[data-role="game-side-lb"] .sg-lblb-body {
    transition: opacity ${FADE_MS}ms ease;
  }
  .sg-lblb[data-role="game-side-lb"][data-fading="1"] .sg-lblb-title,
  .sg-lblb[data-role="game-side-lb"][data-fading="1"] .sg-lblb-body {
    opacity: 0;
  }
`;

function ensureVariantStyles(): void {
  if (document.getElementById('sg-sidelb-variant-style')) return;
  const s = document.createElement('style');
  s.id = 'sg-sidelb-variant-style';
  s.textContent = VARIANT_STYLES;
  document.head.appendChild(s);
}

function medalFor(rank: number): string {
  if (rank === 1) return 'sg-r1';
  if (rank === 2) return 'sg-r2';
  if (rank === 3) return 'sg-r3';
  return '';
}

interface RowState {
  playerId: number;
  displayedScore: number;
  targetScore: number;
  animFrom: number;
  animStartMs: number | null;
  rowEl: HTMLDivElement;
  nameEl: HTMLDivElement;
  scoreEl: HTMLDivElement;
  rankEl: HTMLDivElement;
}

export function createSideLeaderboard(mount: HTMLElement): SideLeaderboardHandle {
  ensureLobbyLbStyles();
  ensureVariantStyles();

  const el = document.createElement('div');
  el.className = 'sg-lblb';
  el.setAttribute('data-role', 'game-side-lb');
  el.setAttribute('data-mode', 'all_time');
  el.innerHTML = `
    <div class="sg-lblb-title" data-testid="ss-side-lb-title">ALL TIME</div>
    <div class="sg-lblb-head"><div>#</div><div>PLAYER</div><div>POINTS</div></div>
    <div class="sg-lblb-body">
      <div class="sg-lblb-scroll" data-role="scroll">
        <div class="sg-lblb-list" data-role="rows">
          <div class="sg-lblb-empty">No scores yet — type your guess in chat!</div>
        </div>
      </div>
    </div>
  `;
  mount.appendChild(el);
  const titleEl = el.querySelector<HTMLDivElement>('.sg-lblb-title')!;
  const body = el.querySelector<HTMLDivElement>('.sg-lblb-body')!;
  const scroll = el.querySelector<HTMLDivElement>('[data-role="scroll"]')!;
  const rowsContainer = el.querySelector<HTMLDivElement>('[data-role="rows"]')!;

  const rowStates = new Map<number, RowState>();
  let raf: number | null = null;
  let isEmpty = true;

  let allTimeRows: LbRow[] = [];
  let sessionRows: LbRow[] = [];
  let mode: LeaderboardMode = 'all_time';
  let cycleTimer: ReturnType<typeof setTimeout> | null = null;

  function buildRow(r: LbRow): RowState {
    const rowEl = document.createElement('div');
    rowEl.className = `sg-lblb-row ${medalFor(r.rank)}`.trim();
    rowEl.innerHTML = `
      <div class="sg-lblb-rank">${r.rank}</div>
      <div class="sg-lblb-name" data-role="name"></div>
      <div class="sg-lblb-points" data-role="score">${r.total_points.toLocaleString()}</div>
    `;
    const rankEl = rowEl.querySelector<HTMLDivElement>('.sg-lblb-rank')!;
    const nameEl = rowEl.querySelector<HTMLDivElement>('[data-role="name"]')!;
    const scoreEl = rowEl.querySelector<HTMLDivElement>('[data-role="score"]')!;
    nameEl.textContent = r.display_name;
    return {
      playerId: r.player_id,
      displayedScore: r.total_points,
      targetScore: r.total_points,
      animFrom: r.total_points,
      animStartMs: null,
      rowEl,
      nameEl,
      scoreEl,
      rankEl,
    };
  }

  function recomputeTravel(): void {
    const contentH = scroll.scrollHeight;
    const containerH = body.clientHeight;
    const overflow = Math.max(0, contentH - containerH);
    scroll.style.setProperty('--sg-lblb-travel', `${-overflow}px`);
    const dur = Math.max(30, Math.round(overflow / 60));
    scroll.style.setProperty('--sg-lblb-duration', `${dur}s`);
  }

  /** Pick the rows for the current mode. */
  function activeRows(): LbRow[] {
    return mode === 'all_time' ? allTimeRows : sessionRows;
  }

  /** Pick the title for the current mode. */
  function titleFor(m: LeaderboardMode): string {
    return m === 'all_time' ? 'ALL TIME' : 'THIS SESSION';
  }

  /** Pick the empty-state message for the current mode. */
  function emptyMessageFor(m: LeaderboardMode): string {
    return m === 'all_time'
      ? 'No scores yet — type your guess in chat!'
      : 'No session scores yet — guess to climb!';
  }

  function applyRows(rows: LbRow[]): void {
    if (rows.length === 0) {
      if (!isEmpty) {
        for (const s of rowStates.values()) s.rowEl.remove();
        rowStates.clear();
        isEmpty = true;
      }
      rowsContainer.innerHTML = `<div class="sg-lblb-empty">${emptyMessageFor(mode)}</div>`;
      requestAnimationFrame(recomputeTravel);
      return;
    }
    if (isEmpty) {
      rowsContainer.innerHTML = '';
      isEmpty = false;
    }
    const seen = new Set<number>();
    const fragment = document.createDocumentFragment();
    for (const r of rows) {
      seen.add(r.player_id);
      let state = rowStates.get(r.player_id);
      if (!state) {
        state = buildRow(r);
        rowStates.set(r.player_id, state);
      } else {
        const desiredCls = `sg-lblb-row ${medalFor(r.rank)}`.trim();
        if (state.rowEl.className !== desiredCls) state.rowEl.className = desiredCls;
        if (state.rankEl.textContent !== String(r.rank)) state.rankEl.textContent = String(r.rank);
        if (state.nameEl.textContent !== r.display_name) state.nameEl.textContent = r.display_name;
      }
      fragment.appendChild(state.rowEl);
      if (state.targetScore !== r.total_points) {
        state.animFrom = state.displayedScore;
        state.targetScore = r.total_points;
        state.animStartMs = performance.now();
      }
      const nameEl = state.nameEl;
      requestAnimationFrame(() => fitText(nameEl, { min: 10, max: 17 }));
    }
    rowsContainer.replaceChildren(fragment);
    for (const [pid, s] of rowStates) {
      if (!seen.has(pid)) {
        s.rowEl.remove();
        rowStates.delete(pid);
      }
    }
    scheduleTick();
    requestAnimationFrame(recomputeTravel);
  }

  /** Apply the active mode's rows + title without animating. */
  function paint(): void {
    titleEl.textContent = titleFor(mode);
    el.setAttribute('data-mode', mode);
    applyRows(activeRows());
  }

  /**
   * Cross-fade to the other mode. Title + body fade out together, then we
   * swap content + fade back in. Total elapsed = 2 × FADE_MS.
   */
  function fadeTo(next: LeaderboardMode): void {
    if (next === mode) return;
    el.setAttribute('data-fading', '1');
    setTimeout(() => {
      mode = next;
      paint();
      el.setAttribute('data-fading', '0');
    }, FADE_MS);
  }

  function scheduleCycle(): void {
    if (cycleTimer) clearTimeout(cycleTimer);
    cycleTimer = setTimeout(() => {
      const next: LeaderboardMode = mode === 'all_time' ? 'session' : 'all_time';
      fadeTo(next);
      scheduleCycle();
    }, CYCLE_MS);
  }

  function scheduleTick(): void {
    if (raf !== null) return;
    raf = requestAnimationFrame(tick);
  }

  function tick(): void {
    raf = null;
    let anyAnimating = false;
    const now = performance.now();
    for (const s of rowStates.values()) {
      if (s.animStartMs === null) continue;
      const t = Math.min(1, (now - s.animStartMs) / 600);
      const e = 1 - Math.pow(1 - t, 3);
      const val = Math.round(s.animFrom + (s.targetScore - s.animFrom) * e);
      if (val !== s.displayedScore) {
        s.displayedScore = val;
        s.scoreEl.textContent = val.toLocaleString();
      }
      if (t >= 1) {
        s.displayedScore = s.targetScore;
        s.scoreEl.textContent = s.targetScore.toLocaleString();
        s.animStartMs = null;
      } else {
        anyAnimating = true;
      }
    }
    if (anyAnimating) scheduleTick();
  }

  function setVisible(v: boolean): void {
    el.classList.toggle('hidden', !v);
    if (v) {
      requestAnimationFrame(recomputeTravel);
      scheduleCycle();
    } else if (cycleTimer) {
      clearTimeout(cycleTimer);
      cycleTimer = null;
    }
  }

  // Initial paint with empty state.
  paint();

  return {
    el,
    setAllTimeRows(rows) {
      allTimeRows = rows;
      if (mode === 'all_time') applyRows(rows);
    },
    setSessionRows(rows) {
      sessionRows = rows;
      if (mode === 'session') applyRows(rows);
    },
    setMode(next) {
      fadeTo(next);
    },
    setVisible,
    dispose() {
      if (raf !== null) cancelAnimationFrame(raf);
      if (cycleTimer) clearTimeout(cycleTimer);
      el.remove();
    },
  };
}
